import { A11yModule } from '@angular/cdk/a11y';
import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { ButtonComponent } from '../../../../../../../../shared/components/button/button.component';

export type InteractionMode = 'Voice to Voice' | 'Text';
export type DialogAction = 'repair_booking' | string;
export type DialogAriaRole = 'dialog' | 'alertdialog';

type DialogButtonConfig = {
  text: string;
  variant: 'primary' | 'secondary';
  mode: InteractionMode;
  action?: DialogAction;
};

export type DialogData = {
  titleText: string;
  descriptionText: string;
  iconName: string;
  isErrorDialog?: boolean;
  ariaRole?: DialogAriaRole;
  autoSwitchCountdownSeconds?: number;
  autoSwitchCountdownPrefix?: string;
  firstButton: DialogButtonConfig;
  secondButton: DialogButtonConfig;
} & Record<string, unknown>;

export type Voice2VoiceDialogResult = {
  mode: InteractionMode;
  action?: DialogAction;
};

@Component({
  selector: 'app-smart-fix-voice2voice-dialog-component',
  standalone: true,
  templateUrl: './voice2voice-dialogs.component.component.html',
  styleUrls: ['./voice2voice-dialogs-component.component.scss'],
  imports: [CommonModule, ButtonComponent, A11yModule],
})
export class Voice2VoiceDialogComponent implements OnDestroy {
  readonly titleId = 'voice2voice-dialog-title';
  readonly descriptionId = 'voice2voice-dialog-description';
  private countdownTimer: ReturnType<typeof setInterval> | null = null;
  countdownSecondsRemaining: number | null = null;

  constructor(
    @Inject(DIALOG_DATA) public data: Partial<DialogData>,
    public dialogRef: DialogRef<Voice2VoiceDialogResult>
  ) {
    const countdown = this.data.autoSwitchCountdownSeconds;
    if (typeof countdown === 'number' && countdown > 0) {
      this.countdownSecondsRemaining = countdown;
      this.countdownTimer = setInterval(() => {
        if (this.countdownSecondsRemaining === null) {
          this.clearCountdownTimer();
          return;
        }

        this.countdownSecondsRemaining = Math.max(0, this.countdownSecondsRemaining - 1);
        if (this.countdownSecondsRemaining <= 0) {
          this.clearCountdownTimer();
        }
      }, 1000);
    }
  }

  ngOnDestroy(): void {
    this.clearCountdownTimer();
  }

  get countdownPrefix(): string {
    return this.data.autoSwitchCountdownPrefix ?? 'We will switch you over in';
  }

  onAction(button?: DialogButtonConfig): void {
    if (!button) return;

    this.dialogRef?.close({
      mode: button.mode,
      action: button.action,
    });
  }

  onClose(): void {
    this.dialogRef?.close();
  }

  private clearCountdownTimer(): void {
    if (!this.countdownTimer) {
      return;
    }

    clearInterval(this.countdownTimer);
    this.countdownTimer = null;
  }
}
